import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import newAmericanFundingImg from "assets/images/newAmericanFundingWhiteBackground.svg";
import equalHousingLogo from "assets/images/equal-housing-opportunity.png";
import { NumericFormat } from "react-number-format";
import { useSelector } from "react-redux";
import { selectPreferredLanguage } from "features/authSlice";
import { NMLSLINK, FOOTER_COPYRIGHT } from "utilities/Constant";
import { getFooterLegalLinks } from "utilities/getFooterLegalLinks";
import {
  getPersistedPreferredLanguage,
  getPreferredLanguage,
  isSpanishLanguage,
} from "utilities/getPreferredLanguage";
import { GLOBAL_LINK_NO_TX_CLASS } from "utilities/globalLinkNoTx";
import { useGetOverallRatingsQuery } from "pages/application/applicationService";
import "./footer.scss";

const ReviewStar = ({ i, avg }: { i: number; avg: number }) => {
  const partial = avg - (i - 1) > 0 && avg - (i - 1) < 1;
  const full = i <= Math.trunc(avg);
  if (partial) return <FontAwesomeIcon icon={solid("star-half-alt")} className="rating__icon" />;
  if (full) return <FontAwesomeIcon icon={solid("star")} className="rating__icon" />;
  return <FontAwesomeIcon icon={solid("star")} className="rating__icon dim" />;
};

const EnglishOnlyBadge = () => (
  <span className={`footer__en-badge ${GLOBAL_LINK_NO_TX_CLASS}`} aria-label="English only">
    EN
  </span>
);

export default function Footer() {
  let footerClassName = "footer";
  if (location.pathname === "/dashboard") {
    footerClassName = "footerdashboard";
  } else if (location.pathname === "/dashboard/documents") {
    footerClassName = "footerdocument";
  }

  const reduxLanguage = useSelector(selectPreferredLanguage);
  const resolvedLanguage = getPersistedPreferredLanguage() ?? reduxLanguage;
  const isSpanishUi = isSpanishLanguage(getPreferredLanguage(resolvedLanguage));
  const legalLinks = getFooterLegalLinks(resolvedLanguage);

  const { data } = useGetOverallRatingsQuery();
  const overall = data?.overallRating;
  const avg = overall?.averageRating ?? 0;
  const count = overall?.reviewCount ?? 0;

  const reviewStars = (averageRating: number) =>
    [1, 2, 3, 4, 5].map((n) => <ReviewStar key={n} i={n} avg={averageRating} />);

  const pluralize = (word: string, c: number) => (c === 1 ? word : `${word}s`);

  return (
    <footer className={footerClassName} role="contentinfo">
      <div className="footerContainer">
        <section className="footer--info no-lo-section">
          <div className="no-loan-officer">
            <div className="no-loan-officer--info">
              <img className="newamerican-funding-logo" src={newAmericanFundingImg} alt="New American Funding" />
              <img className="equal-housing-logo" src={equalHousingLogo} alt="Equal Housing Lender" />

              <div className="ally-row">
                <FontAwesomeIcon icon={solid("universal-access")} className="person-limbs-wide" />
                <p className="contact">
                  If you are using a screen reader or other auxiliary aid and are having problems using this website, please call{" "}
                  <a href="tel:8004502010">800-450-2010</a>{'. Ext. 7100 for assistance.'}
                </p>
              </div>
            </div>
          </div>

          <p className="copyright-text">{FOOTER_COPYRIGHT}</p>

          <ul className="links">
            <li>
              <a href={NMLSLINK} target="_blank" rel="noreferrer">
                NMLS ID #6606
              </a>
              {isSpanishUi && <EnglishOnlyBadge />}
            </li>
            <li>
              <a href={legalLinks.stateLicensing} target="_blank" rel="noreferrer">
                State Licensing
              </a>
              {isSpanishUi && <EnglishOnlyBadge />}
            </li>
            <li>
              <a href={legalLinks.termsOfUse} target="_blank" rel="noreferrer">
                Terms of Use
              </a>
            </li>
            <li>
              <a href={legalLinks.privacyPolicy} target="_blank" rel="noreferrer">
                Privacy Policy
              </a>
            </li>
            <li>
              <a href={legalLinks.electronicConsent} target="_blank" rel="noreferrer">
                Electronic Consent
              </a>
            </li>
            <li>
              <a href={legalLinks.californiaCollectionNotice} target="_blank" rel="noreferrer">
                CA Collection Notice
              </a>
            </li>
            <li>
              <a href={legalLinks.cookiePolicy} target="_blank" rel="noreferrer">
                Cookie Policy
              </a>
            </li>
            <li>
              <a href={legalLinks.partners} target="_blank" rel="noreferrer">
                Partners
              </a>
            </li>
          </ul>
          {overall && (
            <section className="rating" aria-label="Overall rating">
              <div className="reviews reviews--footer">
                <div className="reviews__ratings">
                  <div className="ratingline">
                    <span className="reviews__stars">{reviewStars(avg)}</span>
                    <div className="ratingAll">
                      <span className="rating__value">{avg}</span>
                      <span className="rating__slash">/5</span>
                      <span className="rating__on">&nbsp;on&nbsp;Experience.com</span>
                      <span className="rating__count-inline">
                        &nbsp;(
                        <NumericFormat value={count} displayType="text" thousandSeparator />
                        &nbsp;
                        {pluralize("review", count)})
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          )}
        </section>
      </div>
    </footer>
  );
}
